import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle, Warning, Lightbulb, ChartBar } from "@phosphor-icons/react";

const DIM_LABELS = {
  brand_consistency: "Brand Consistency", sales_potential: "Sales Potential",
  engagement_potential: "Engagement Potential", cta_effectiveness: "CTA Effectiveness",
  audience_match: "Audience Match", seasonal_relevance: "Seasonal Relevance",
  product_selection: "Product Selection", seo_strength: "SEO Strength", deliverability: "Email Deliverability",
};
const barColor = (v) => (v >= 85 ? "bg-emerald-400" : v >= 70 ? "bg-sky-400" : v >= 55 ? "bg-amber-400" : "bg-red-400");
export const gradeColor = (g) => (String(g).startsWith("A") ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
  : String(g).startsWith("B") ? "bg-sky-500/15 text-sky-300 border-sky-500/30"
  : String(g).startsWith("C") ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
  : "bg-red-500/15 text-red-300 border-red-500/30");

export const ScoreBars = ({ scores = {}, reasons = {} }) => (
  <div className="space-y-2.5">
    {Object.entries(scores).map(([k, v]) => (
      <div key={k} data-testid={`score-dim-${k}`}>
        <div className="flex items-center justify-between text-xs">
          <span className="text-[#cfcfcf]">{DIM_LABELS[k] || k}</span>
          <span className="font-bold text-white">{v}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/8 mt-1"><div className={`h-1.5 rounded-full ${barColor(v)}`} style={{ width: `${v}%` }} /></div>
        {reasons[k] && <div className="text-[10px] text-[#7a7a7a] mt-0.5">{reasons[k]}</div>}
      </div>
    ))}
  </div>
);

export const MarketingScoreDialog = ({ open, onClose, title, score }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="marketing-score-dialog">
      <DialogHeader>
        <DialogTitle className="font-head flex items-center gap-2"><ChartBar size={18} className="brand-text" weight="fill" /> AI Marketing Score</DialogTitle>
        <DialogDescription className="text-[#9a9a9a] truncate">{title}</DialogDescription>
      </DialogHeader>
      {score && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span data-testid="score-grade" className={`text-2xl font-head font-extrabold px-3 py-1 rounded-lg border ${gradeColor(score.grade)}`}>{score.grade}</span>
            <div>
              <div className="text-sm font-semibold">Overall: {score.overall}/100</div>
              <div className="text-[10px] text-[#7a7a7a]">Scored {String(score.scored_at || score.generated_at || "").slice(0, 10)}</div>
            </div>
          </div>
          <ScoreBars scores={score.scores} reasons={score.reasons} />
          {!!(score.improvements || []).length && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-1.5 flex items-center gap-1"><Lightbulb size={12} className="text-amber-300" /> Improvements before publishing</div>
              <ul className="space-y-1">
                {score.improvements.map((s, i) => (
                  <li key={i} data-testid={`score-improvement-${i}`} className="text-xs text-[#cfcfcf] flex gap-1.5"><span className="text-amber-300">→</span>{s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const CampaignIntelDialog = ({ open, onClose, name, report }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto" data-testid="campaign-intel-dialog">
      <DialogHeader>
        <DialogTitle className="font-head flex items-center gap-2"><ChartBar size={18} className="brand-text" weight="fill" /> Campaign Intelligence</DialogTitle>
        <DialogDescription className="text-[#9a9a9a] truncate">{name}</DialogDescription>
      </DialogHeader>
      {report && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span data-testid="campaign-grade" className={`text-2xl font-head font-extrabold px-3 py-1 rounded-lg border ${gradeColor(report.grade)}`}>{report.grade}</span>
            <div className="text-sm font-semibold">Overall Campaign Grade · {report.overall}/100 · {report.asset_count} assets</div>
          </div>
          <p data-testid="campaign-intel-summary" className="text-sm text-[#e6e6e6] leading-relaxed border-l-2 border-[#6B3DFF] pl-3">{report.summary}</p>
          {report.estimates && (
            <div className="text-xs text-[#9a9a9a]">
              Projected: reach <b className="text-white">{(report.estimates.reach || 0).toLocaleString()}</b> · engagement <b className="text-white">{(report.estimates.engagement || 0).toLocaleString()}</b> · clicks <b className="text-white">{(report.estimates.clicks || 0).toLocaleString()}</b> <span className="text-[9px]">(AI estimates — improves with real data)</span>
            </div>
          )}
          <ScoreBars scores={report.scores} reasons={report.reasons} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-1.5 flex items-center gap-1"><CheckCircle size={12} className="text-emerald-400" /> Strengths</div>
              <ul className="space-y-1">{(report.strengths || []).map((s, i) => <li key={i} className="text-xs text-[#cfcfcf]">• {s}</li>)}</ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-1.5 flex items-center gap-1"><Warning size={12} className="text-amber-300" /> Potential weaknesses</div>
              <ul className="space-y-1">{(report.weaknesses || []).map((s, i) => <li key={i} className="text-xs text-[#cfcfcf]">• {s}</li>)}</ul>
            </div>
          </div>
          {!!(report.improvements || []).length && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-1.5 flex items-center gap-1"><Lightbulb size={12} className="text-amber-300" /> Recommended improvements</div>
              <ul className="space-y-1">{report.improvements.map((s, i) => <li key={i} className="text-xs text-[#cfcfcf] flex gap-1.5"><span className="text-amber-300">→</span>{s}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>
);
